/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  signal,
  Signal,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { Product } from '@app/akita/api/products/models/product.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';

import { TranslocoModule } from '@ngneat/transloco';
import { RouterModule } from '@angular/router';

// Components
import { FeaturedBreadcrumbsComponent } from '@app/featured/components/featured-breadcrumbs/featured-breadcrumbs.component';
import { InViewportModule } from 'ng-in-viewport';
import { UiSkeletonComponent } from '@app/shared/components/ui-skeleton/ui-skeleton.component';
import { FeaturedListingDetailsCardComponent } from '@app/listing-details/components/featured-listing-details-card/featured-listing-details-card.component';
import { FeaturedReviewsCardComponent } from '@app/featured/components/featured-reviews-card/featured-reviews-card.component';
import { BoughtTogetherBannerComponent } from '../../products/components/bought-together-banner/bought-together-banner.component';
import { ComingSoonBannerComponent } from '../../home-new/components/coming-soon-banner/coming-soon-banner.component';
import { AkitaRemoteConfigQuery } from '@app/akita/api/remote-config/state/remote-config.query';
import { toSignal } from '@angular/core/rxjs-interop';
import { BenefitsBannerComponent } from '../../home-new/components/benefits-banner/benefits-banner.component';
import { CategoryPickerComponent } from '../../home-new/components/category-picker/category-picker.component';
import { BestSellersPickerComponent } from '../best-sellers-picker/best-sellers-picker.component';

@Component({
  selector: 'app-variant-product-card',
  templateUrl: './variant-product-card.template.html',
  styleUrls: ['./variant-product-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    FeaturedListingDetailsCardComponent,
    UiSkeletonComponent,
    FeaturedReviewsCardComponent,
    FeaturedBreadcrumbsComponent,
    InViewportModule,
    BoughtTogetherBannerComponent,
    ComingSoonBannerComponent,
    BenefitsBannerComponent,
    CategoryPickerComponent,
    BestSellersPickerComponent,
  ],
})
export class VariantProductCardComponent implements OnChanges, OnDestroy {
  @Input() public product?: Product | null;
  @Input() public variant?: string | null;

  public readonly lessThan$: Observable<ScreenSizeInfoState>;
  public isBrowser: boolean;

  public isGoogleBotUserAgent: boolean;
  public enableBuybackFeature: Signal<boolean | null> = signal(null);

  private readonly picturesSubject: BehaviorSubject<
    Array<{
      src: string;
      thumb: string;
    }>
  >;
  public readonly pictures$: Observable<
    Array<{
      src: string;
      thumb: string;
    }>
  >;

  @ViewChild('componentWrapper', { read: ViewContainerRef })
  public componentWrapper!: ViewContainerRef;

  public priceBannerLoaded = false;
  public whyBannerLoaded = false;

  private readonly descriptionSubject: BehaviorSubject<SafeHtml>;
  public readonly description$: Observable<SafeHtml>;
  private readonly subscriptions: Subscription;
  private readonly destroySubject: Subject<void>;

  private readonly specsSubject: BehaviorSubject<Array<{ key: string; value: string }>>;
  public readonly specs$: Observable<Array<{ key: string; value: string }>>;

  constructor(
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaRemoteConfigQuery: AkitaRemoteConfigQuery
  ) {
    this.destroySubject = new Subject<void>();
    this.subscriptions = new Subscription();
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.isBrowser = this.akitaRouterQuery.isBrowser;

    this.enableBuybackFeature = toSignal(
      this.akitaRemoteConfigQuery.selectEnableBuybackFeature(),
      {
        initialValue: null,
      }
    );

    this.isGoogleBotUserAgent = this.akitaRouterQuery.isGoogleBotUserAgent;

    this.picturesSubject = new BehaviorSubject([] as any);
    this.pictures$ = this.picturesSubject.asObservable();

    this.descriptionSubject = new BehaviorSubject('' as any);
    this.description$ = this.descriptionSubject.asObservable();

    this.specsSubject = new BehaviorSubject([] as any);
    this.specs$ = this.specsSubject.asObservable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      const newPictures = new Array(0);
      for (const picture of this.product?.images || []) {
        newPictures.push({
          src: `${picture || ''}`,
          thumb: `${picture || ''}`,
        });
      }
      this.picturesSubject.next(newPictures);
    }
  }

  public trackByKeyVal(index: number, value?: { key: string; value: string }): string {
    return `${value?.key || index}${value?.value || index}`;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
